import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import { GlobalContext } from "../../context/GlobalContext";

const PAGE_SIZE = 10;

function shareOrigin() {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

export default function MusicLibraryPanel({ lang, onUseLyricInStudio, linkedLyricJobId, onClearLinkedLyric }) {
  const theme = useTheme();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [tab, setTab] = React.useState("lyrics");
  const [lyrics, setLyrics] = React.useState([]);
  const [songs, setSongs] = React.useState([]);
  const [lyricsPage, setLyricsPage] = React.useState(1);
  const [songsPage, setSongsPage] = React.useState(1);
  const [lyricsPagination, setLyricsPagination] = React.useState(null);
  const [songsPagination, setSongsPagination] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editText, setEditText] = React.useState("");
  const [editTitle, setEditTitle] = React.useState("");
  /** Per-row accordion open state (multiple rows can be expanded). */
  const [lyricAccOpen, setLyricAccOpen] = React.useState({});
  const [songAccOpen, setSongAccOpen] = React.useState({});

  const loadLyrics = React.useCallback(async () => {
    setLoading(true);
    const res = await hitAxios({
      path: `/api/music/lyrics/jobs?page=${lyricsPage}&limit=${PAGE_SIZE}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    setLoading(false);
    if (!res?.data?.success || !Array.isArray(res.data.data?.jobs)) return;
    const pag = res.data.data.pagination;
    const jobs = res.data.data.jobs;
    if (pag) {
      if (pag.total === 0) {
        setLyrics([]);
        setLyricsPagination(pag);
        if (lyricsPage !== 1) setLyricsPage(1);
        return;
      }
      if (lyricsPage > pag.total_pages) {
        setLyricsPage(pag.total_pages);
        return;
      }
    }
    setLyrics(jobs);
    setLyricsPagination(pag || null);
  }, [hitAxios, lyricsPage]);

  const loadSongs = React.useCallback(async () => {
    setLoading(true);
    const res = await hitAxios({
      path: `/api/music/songs/jobs?page=${songsPage}&limit=${PAGE_SIZE}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    setLoading(false);
    if (!res?.data?.success || !Array.isArray(res.data.data?.jobs)) return;
    const pag = res.data.data.pagination;
    const jobs = res.data.data.jobs;
    if (pag) {
      if (pag.total === 0) {
        setSongs([]);
        setSongsPagination(pag);
        if (songsPage !== 1) setSongsPage(1);
        return;
      }
      if (songsPage > pag.total_pages) {
        setSongsPage(pag.total_pages);
        return;
      }
    }
    setSongs(jobs);
    setSongsPagination(pag || null);
  }, [hitAxios, songsPage]);

  React.useEffect(() => {
    if (tab === "lyrics") loadLyrics();
  }, [tab, lyricsPage, loadLyrics]);

  React.useEffect(() => {
    if (tab === "songs") loadSongs();
  }, [tab, songsPage, loadSongs]);

  React.useEffect(() => {
    if (tab === "lyrics") setSongAccOpen({});
    else setLyricAccOpen({});
  }, [tab]);

  async function toggleLyricShare(row, next) {
    const res = await hitAxios({
      path: `/api/music/lyrics/jobs/${row.id}/share`,
      post: true,
      obj: { is_public: next },
      showLoading: false,
      showSnackbar: false,
    });
    if (!res?.data?.success) {
      showSnack?.(res?.data?.msg || "Update failed", false);
      return;
    }
    showSnack?.(next ? (lang?.musicShareOn || "Sharing on") : (lang?.musicShareOff || "Sharing off"), true);
    loadLyrics();
  }

  async function toggleSongShare(row, next) {
    const res = await hitAxios({
      path: `/api/music/songs/jobs/${row.id}/share`,
      post: true,
      obj: { is_public: next },
      showLoading: false,
      showSnackbar: false,
    });
    if (!res?.data?.success) {
      showSnack?.(res?.data?.msg || "Update failed", false);
      return;
    }
    showSnack?.(next ? (lang?.musicShareOn || "Sharing on") : (lang?.musicShareOff || "Sharing off"), true);
    loadSongs();
  }

  function copyShareLyric(row) {
    if (!row.share_token) return;
    const url = `${shareOrigin()}/share/music/lyric/${row.share_token}`;
    navigator.clipboard?.writeText(url).then(
      () => showSnack?.(lang?.musicShareCopied || "Link copied", true),
      () => showSnack?.("Copy failed", false),
    );
  }

  function copyShareSong(row) {
    if (!row.share_token) return;
    const url = `${shareOrigin()}/share/music/song/${row.share_token}`;
    navigator.clipboard?.writeText(url).then(
      () => showSnack?.(lang?.musicShareCopied || "Link copied", true),
      () => showSnack?.("Copy failed", false),
    );
  }

  async function deleteLyric(id) {
    const res = await hitAxios({
      path: `/api/music/lyrics/jobs/${id}`,
      method: "DELETE",
      obj: {},
      showLoading: false,
      showSnackbar: false,
    });
    if (!res?.data?.success) {
      showSnack?.(res?.data?.msg || "Delete failed", false);
      return;
    }
    loadLyrics();
  }

  async function deleteSong(id) {
    const res = await hitAxios({
      path: `/api/music/songs/jobs/${id}`,
      method: "DELETE",
      obj: {},
      showLoading: false,
      showSnackbar: false,
    });
    if (!res?.data?.success) {
      showSnack?.(res?.data?.msg || "Delete failed", false);
      return;
    }
    loadSongs();
  }

  function openEdit(row) {
    setEditRow(row);
    setEditTitle(row.title || "");
    setEditText(row.lyrics_text || "");
    setEditOpen(true);
  }

  async function saveEdit() {
    if (!editRow) return;
    const res = await hitAxios({
      path: `/api/music/lyrics/jobs/${editRow.id}`,
      method: "PATCH",
      obj: { title: editTitle, lyrics_text: editText },
      showLoading: false,
      showSnackbar: false,
    });
    if (!res?.data?.success) {
      showSnack?.(res?.data?.msg || "Save failed", false);
      return;
    }
    setEditOpen(false);
    loadLyrics();
    showSnack?.(lang?.musicLyricSaved || "Saved", true);
  }

  const rowAccordionSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    "&:before": { display: "none" },
    boxShadow: "none",
    bgcolor: alpha(theme.palette.background.paper, 0.6),
    "&.Mui-expanded": { margin: 0 },
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="lyrics" label={lang?.musicLibMyMusic || lang?.musicLibLyrics || "My music"} />
          <Tab value="songs" label={lang?.musicLibSongs || "My songs"} />
        </Tabs>
        <Button size="small" variant="outlined" disabled={loading} onClick={() => (tab === "lyrics" ? loadLyrics() : loadSongs())}>
          {lang?.musicLibRefresh || "Refresh"}
        </Button>
      </Stack>

      {linkedLyricJobId != null && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={(lang?.musicLinkedLyric || "Linked lyric for next generate") + ` #${linkedLyricJobId}`}
            onDelete={onClearLinkedLyric}
          />
        </Stack>
      )}

      {tab === "lyrics" && (
        <Stack spacing={1.5}>
          {lyrics.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary">
              {lang?.musicLibEmptyLyrics || "No saved lyrics yet. Use Studio → Make lyrics to create one."}
            </Typography>
          )}
          {lyrics.map((row) => (
            <Accordion
              key={row.id}
              expanded={Boolean(lyricAccOpen[row.id])}
              onChange={(_, isExpanded) =>
                setLyricAccOpen((prev) => ({ ...prev, [row.id]: isExpanded }))
              }
              sx={{ ...rowAccordionSx, mb: 1.25 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  minHeight: 48,
                  "& .MuiAccordionSummary-content": { alignItems: "center", my: 1, gap: 1, overflow: "hidden" },
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {row.title || `Lyric #${row.id}`}
                </Typography>
                <Chip size="small" label={row.status} variant="outlined" sx={{ flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  #{row.id}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, borderTop: `1px solid ${theme.palette.divider}` }}>
                {row.genre_id != null && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {`${lang?.musicGenreSelectLabel || "Genre"}: ${row.genre_id}`}
                  </Typography>
                )}
                {row.lyrics_text ? (
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, maxHeight: 240, overflow: "auto", whiteSpace: "pre-wrap" }}
                    color="text.secondary"
                  >
                    {row.lyrics_text}
                  </Typography>
                ) : null}
                {row.error_message && (
                  <Typography variant="caption" color="error" display="block" sx={{ mb: 1.5 }}>
                    {row.error_message}
                  </Typography>
                )}
                <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
                  <Button
                    size="small"
                    variant="text"
                    disabled={row.status !== "ready" || !String(row.lyrics_text || "").trim()}
                    onClick={() =>
                      onUseLyricInStudio?.({
                        id: row.id,
                        title: row.title,
                        lyrics_text: row.lyrics_text,
                        genre_id: row.genre_id,
                      })
                    }
                  >
                    {lang?.musicUseInStudio || "Use in studio"}
                  </Button>
                  <Button size="small" variant="text" disabled={row.status !== "ready"} onClick={() => openEdit(row)}>
                    {lang?.musicEdit || "Edit"}
                  </Button>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption">{lang?.musicPublic || "Public"}</Typography>
                    <Switch
                      size="small"
                      checked={Boolean(row.is_public)}
                      onChange={(e) => toggleLyricShare(row, e.target.checked)}
                      disabled={row.status !== "ready"}
                    />
                  </Stack>
                  <Tooltip title={lang?.musicCopyShareLink || "Copy share link"}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!row.is_public || !row.share_token}
                        onClick={() => copyShareLyric(row)}
                      >
                        <ContentCopyOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={lang?.musicOpenShare || "Open public page"}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!row.is_public || !row.share_token}
                        component="a"
                        href={`/share/music/lyric/${row.share_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={lang?.musicDelete || "Delete"}>
                    <IconButton size="small" onClick={() => deleteLyric(row.id)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
          {lyricsPagination && lyricsPagination.total > 0 && (
            <Stack alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {(lang?.musicLibPaginationSummary || "Showing {{from}}–{{to}} of {{total}}")
                  .replace("{{from}}", String((lyricsPage - 1) * lyricsPagination.limit + 1))
                  .replace("{{to}}", String(Math.min(lyricsPage * lyricsPagination.limit, lyricsPagination.total)))
                  .replace("{{total}}", String(lyricsPagination.total))}
              </Typography>
              <Pagination
                count={Math.max(1, lyricsPagination.total_pages)}
                page={Math.min(lyricsPage, Math.max(1, lyricsPagination.total_pages))}
                onChange={(_, p) => {
                  setLyricAccOpen({});
                  setLyricsPage(p);
                }}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Stack>
          )}
        </Stack>
      )}

      {tab === "songs" && (
        <Stack spacing={1.5}>
          {songs.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary">
              {lang?.musicLibEmptySongs || "No saved songs yet. Generate music from the Studio tab."}
            </Typography>
          )}
          {songs.map((row) => (
            <Accordion
              key={row.id}
              expanded={Boolean(songAccOpen[row.id])}
              onChange={(_, isExpanded) =>
                setSongAccOpen((prev) => ({ ...prev, [row.id]: isExpanded }))
              }
              sx={{ ...rowAccordionSx, mb: 1.25 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  minHeight: 48,
                  "& .MuiAccordionSummary-content": { alignItems: "center", my: 1, gap: 1, overflow: "hidden" },
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {row.title || `Song #${row.id}`}
                </Typography>
                <Chip size="small" label={row.status} variant="outlined" sx={{ flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  #{row.id}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, borderTop: `1px solid ${theme.palette.divider}` }}>
                {row.lyric_id != null && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {`${lang?.musicLinkedLyricShort || "Lyric"} #${row.lyric_id}`}
                  </Typography>
                )}
                {row.error_message && (
                  <Typography variant="caption" color="error" display="block" sx={{ mb: 1.5 }}>
                    {row.error_message}
                  </Typography>
                )}
                {Array.isArray(row.tracks) &&
                  row.tracks.map((tr, i) => (
                    <Box key={tr.stored || i} sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {tr.title || `Track ${i + 1}`}
                      </Typography>
                      {tr.stored ? (
                        <Box component="audio" controls src={tr.stored} sx={{ width: "100%", display: "block", mt: 0.5 }} />
                      ) : null}
                    </Box>
                  ))}
                <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center" sx={{ mt: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption">{lang?.musicPublic || "Public"}</Typography>
                    <Switch
                      size="small"
                      checked={Boolean(row.is_public)}
                      onChange={(e) => toggleSongShare(row, e.target.checked)}
                      disabled={row.status !== "completed"}
                    />
                  </Stack>
                  <Tooltip title={lang?.musicCopyShareLink || "Copy share link"}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!row.is_public || !row.share_token}
                        onClick={() => copyShareSong(row)}
                      >
                        <ContentCopyOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={lang?.musicOpenShare || "Open public page"}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!row.is_public || !row.share_token}
                        component="a"
                        href={`/share/music/song/${row.share_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={lang?.musicDelete || "Delete"}>
                    <IconButton size="small" onClick={() => deleteSong(row.id)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
          {songsPagination && songsPagination.total > 0 && (
            <Stack alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {(lang?.musicLibPaginationSummary || "Showing {{from}}–{{to}} of {{total}}")
                  .replace("{{from}}", String((songsPage - 1) * songsPagination.limit + 1))
                  .replace("{{to}}", String(Math.min(songsPage * songsPagination.limit, songsPagination.total)))
                  .replace("{{total}}", String(songsPagination.total))}
              </Typography>
              <Pagination
                count={Math.max(1, songsPagination.total_pages)}
                page={Math.min(songsPage, Math.max(1, songsPagination.total_pages))}
                onChange={(_, p) => {
                  setSongAccOpen({});
                  setSongsPage(p);
                }}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Stack>
          )}
        </Stack>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{lang?.musicEditLyricTitle || "Edit lyrics"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={lang?.musicTitleField || "Title"}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={12}
            label={lang?.musicLyricsOrPrompt || "Lyrics"}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{lang?.musicCancel || "Cancel"}</Button>
          <Button variant="contained" onClick={saveEdit}>
            {lang?.musicSave || "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
